import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { lockColumn } from '@customer/components/tables/grid/columnDefs';
import { ColDef, ITooltipParams, ValueGetterParams } from 'ag-grid-community';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { ObjectModel, RemoteObjectModel } from '@customer/store/apis/interactionsApi';
import LightTextRenderer from '@cas/components/tables/cells/light-text/LightTextRenderer';
import { CustomCellRendererProps } from 'ag-grid-react';
import AssetGroupCellRenderer from '@customer/components/tables/cells/asset-group/AssetGroupCellRenderer';
import SpeedRangeRenderer from '@customer/components/tables/cells/speed-range/SpeedRangeRenderer';
import { DefaultCell } from '@dmc/ui/tables';
import SpeedRenderer from '../cells/speed/SpeedRenderer';
import { getObjectName, getObjectType } from '@cas/utils/translationHelpers';
import { EmptyCell } from '../formatters/genericFormatters';
import AssetNameRenderer from '../cells/asset-name/AssetNameRenderer';
import DotNetixNameTooltip from '../tooltips/dotnetix-name-tooltip/DotNetixNameTooltip';

enum ComparisonRowType {
  Text,
  AssetGroups,
  AssetNames,
  Speed,
  SpeedRange,
}

type ObjectValue =
  | string
  | number
  | [number, number]
  | [number | null, number | null]
  | number[]
  | [string | undefined, boolean]
  | null;

interface BaseRow {
  type: ComparisonRowType;
  label: string;
  loValue?: ObjectValue;
  roValue?: ObjectValue;
}

interface TextRow extends BaseRow {
  type: ComparisonRowType.Text;
  loValue?: string;
  roValue?: string;
}

interface AssetNamesRow extends BaseRow {
  type: ComparisonRowType.AssetNames;
  loValue?: [string | undefined, boolean];
  roValue?: [string | undefined, boolean];
}

interface AssetGroupsRow extends BaseRow {
  type: ComparisonRowType.AssetGroups;
  loValue?: number[];
  roValue?: number[];
}

interface SpeedRow extends BaseRow {
  type: ComparisonRowType.Speed;
  loValue?: number | null;
  roValue?: number | null;
}

interface SpeedRangeRow extends BaseRow {
  type: ComparisonRowType.SpeedRange;
  loValue?: [number, number];
  roValue?: [number, number];
}

type ComparisonRow = TextRow | AssetGroupsRow | AssetNamesRow | SpeedRow | SpeedRangeRow;

interface BaseCell {
  objectValue?: ObjectValue;
  type?: ComparisonRowType;
}

interface TextCell extends BaseCell {
  objectValue?: string;
  type?: ComparisonRowType.Text;
}

interface AssetNameCell extends BaseCell {
  objectValue?: [string | undefined, boolean];
  type?: ComparisonRowType.AssetNames;
}

interface AssetGroupsCell extends BaseCell {
  objectValue?: number[];
  type?: ComparisonRowType.AssetGroups;
}

interface SpeedCell extends BaseCell {
  objectValue?: number | null;
  type?: ComparisonRowType.Speed;
}

interface SpeedRangeCell extends BaseCell {
  objectValue?: [number, number];
  type?: ComparisonRowType.SpeedRange;
}

type ComparisonCellValue = TextCell | AssetGroupsCell | AssetNameCell | SpeedCell | SpeedRangeCell;

const loValueGetter = ({ data }: ValueGetterParams<ComparisonRow>): BaseCell => ({
  objectValue: data?.loValue,
  type: data?.type,
});

const roValueGetter = ({ data }: ValueGetterParams<ComparisonRow>): BaseCell => ({
  objectValue: data?.roValue,
  type: data?.type,
});

const tooltipValueGetter = ({ data }: ITooltipParams<ComparisonRow>): boolean => {
  if (data?.type === ComparisonRowType.AssetNames && data?.roValue !== undefined) {
    return data.roValue[1];
  }
  return false;
};

const getSpeedRange = (objectModel?: ObjectModel | null): [number, number] | undefined => {
  if (
    objectModel &&
    typeof objectModel.minSpd === 'number' &&
    typeof objectModel.maxSpd === 'number'
  ) {
    return [objectModel.minSpd, objectModel.maxSpd];
  }

  return undefined;
};

const ObjectRenderer = ({
  value,
  ...otherProps
}: CustomCellRendererProps<ComparisonRow, ComparisonCellValue>): ReactElement | string | null => {
  if (value) {
    const { objectValue, type } = value;
    if (typeof type !== 'undefined' && typeof objectValue !== 'undefined') {
      switch (type) {
        case ComparisonRowType.Text:
          return <DefaultCell {...otherProps} value={objectValue} />;
        case ComparisonRowType.AssetNames:
          return <AssetNameRenderer value={objectValue} />;
        case ComparisonRowType.AssetGroups:
          return <AssetGroupCellRenderer value={objectValue} />;
        case ComparisonRowType.Speed:
          return <SpeedRenderer value={objectValue} />;
        case ComparisonRowType.SpeedRange:
          return <SpeedRangeRenderer value={objectValue} />;
      }
    }
  }

  return EmptyCell;
};

const getRowId = (data: ComparisonRow) => data.label;

interface ObjectComparisonTableProps {
  sliceKey: string;
  lo?: ObjectModel;
  ro?: RemoteObjectModel | null;
}

export function ObjectComparisonTable({ sliceKey, lo, ro }: ObjectComparisonTableProps) {
  const { t } = useTranslation();

  const rowData: ComparisonRow[] = useMemo(
    () => [
      {
        label: t('Asset'),
        loValue: [lo?.n, false],
        roValue: [getObjectName(t, ro?.n, ro?.roDN), ro?.roDN ?? false],
        type: ComparisonRowType.AssetNames,
      },
      {
        label: t('ObjectType'),
        loValue: getObjectType(t, lo?.obj),
        roValue: getObjectType(t, ro?.obj),
        type: ComparisonRowType.Text,
      },
      {
        label: t('AssetGroup'),
        loValue: lo?.ags,
        roValue: ro?.ags,
        type: ComparisonRowType.AssetGroups,
      },
      {
        label: t('EntrySpeed'),
        loValue: lo?.spd,
        roValue: ro?.spd,
        type: ComparisonRowType.Speed,
      },
      {
        label: t('MinMaxSpeed'),
        loValue: getSpeedRange(lo),
        roValue: getSpeedRange(ro),
        type: ComparisonRowType.SpeedRange,
      },
    ],
    [t, lo, ro],
  );

  const columnDefs: ColDef<ComparisonRow>[] = useMemo(
    () => [
      {
        field: 'label',
        headerName: '',
        cellRenderer: LightTextRenderer,
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: t('LO'),
        valueGetter: loValueGetter,
        cellRenderer: ObjectRenderer,
        flex: 1,
      },
      {
        headerName: t('RO'),
        valueGetter: roValueGetter,
        cellRenderer: ObjectRenderer,
        tooltipValueGetter: tooltipValueGetter,
        tooltipComponent: DotNetixNameTooltip,
        flex: 1,
      },
    ],
    [t],
  );

  return (
    <GridControlled<ComparisonRow>
      sliceKey={sliceKey}
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={lockColumn}
      getRowId={getRowId}
      autoheight
    />
  );
}

export default ObjectComparisonTable;
